/**
 * Review controls for the contract editor.
 *
 *   - status='pending': a hint pointing at the StatusBar's
 *     "Skicka för granskning" button (the picker itself lives in the
 *     StatusBar so it's reachable without scrolling).
 *   - status='in_review':
 *     - If actor is the reviewer: approve / reject (with comment).
 *     - Otherwise: the open review's reviewer + comment + cancel.
 *   - Always: surface the most recent review's outcome (esp. a
 *     rejection comment so the requester sees why their last
 *     submission was sent back).
 */

import { useCallback, useState } from 'react';
import {
  tenantApi,
  type ContractDetail,
  type ContractReview,
} from '../api.js';
import { useInvalidate, useTenantQuery } from '../query.js';
import { STALE_APPROVAL_NOTICE } from '../lib/approval-reset.js';

export function ReviewPanel({
  token,
  contract,
  actorId,
  historyOpen = false,
}: {
  token: string;
  contract: ContractDetail;
  actorId: string;
  /** Force the review-history disclosure open — used when the panel is
   *  shown in its own "Granskning" tab rather than the compact header. */
  historyOpen?: boolean;
}) {
  const invalidate = useInvalidate();
  const reviewsQ = useTenantQuery(
    'reviews.list_for_contract',
    (cid) =>
      tenantApi.reviews.list_for_contract({ contract_id: cid as string }),
    [contract.id],
    { ignoreActiveGroup: true, revalidateOnFocus: false },
  );

  const reviews: ContractReview[] = reviewsQ.data?.reviews ?? [];
  const activeReview = reviews.find((r) => r.status === 'pending') ?? null;
  const lastClosed =
    reviews.find((r) => r.status !== 'pending') ?? null;

  const onChanged = useCallback(async () => {
    await Promise.all([
      invalidate('reviews.list_for_contract', contract.id),
      invalidate('contracts.get', contract.id),
      invalidate('contracts.list'),
      invalidate('reviews.list_my_pending'),
    ]);
  }, [invalidate, contract.id]);

  if (reviewsQ.isLoading) {
    return <div className="text-xs text-stone-400">Laddar granskning…</div>;
  }

  // Nothing to show: draft contract with no historical reviews and no
  // active one. The header carries the editing affordances; rendering
  // an empty "Granskning" card here would just be visual noise.
  if (
    contract.status === 'draft' &&
    !activeReview &&
    !lastClosed &&
    reviews.length === 0
  ) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="border-b border-stone-200 pb-1 text-xs font-medium uppercase tracking-wider text-stone-500">
        Granskning
      </div>

      {/* Approval banner stays inside the tab — approved reviews are
          archival; rejections are surfaced at the top of the page (see
          ContractEditorPage) so the requester sees the comment without
          a tab click. */}
      {lastClosed?.status === 'approved' &&
        contract.status === 'pending' &&
        !activeReview && (
          <ClosedReviewBanner review={lastClosed} variant="approved" />
        )}

      {/* ADR-0102 decision 3: a change after approval made it stale. */}
      {!activeReview && lastClosed?.status === 'superseded' && (
        <div className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
          {STALE_APPROVAL_NOTICE}
        </div>
      )}

      {!activeReview && contract.status === 'pending' && (
        <div className="rounded border border-stone-200 bg-stone-50 p-2 text-xs text-stone-600">
          Använd knappen <span className="font-medium">Skicka för granskning</span>{' '}
          i topplisten för att tilldela en granskare.
        </div>
      )}

      {activeReview && (
        <ActiveReviewCard
          token={token}
          review={activeReview}
          actorId={actorId}
          onChanged={onChanged}
        />
      )}

      {reviews.length > 0 && (
        <ReviewHistory
          reviews={reviews}
          activeReviewId={activeReview?.id ?? null}
          open={historyOpen}
        />
      )}
    </div>
  );
}

function ActiveReviewCard({
  token,
  review,
  actorId,
  onChanged,
}: {
  token: string;
  review: ContractReview;
  actorId: string;
  onChanged: () => Promise<void>;
}) {
  const isReviewer = review.reviewer.id === actorId;
  const isRequester = review.requester.id === actorId;

  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState<null | 'approve' | 'reject' | 'cancel'>(null);
  const [err, setErr] = useState<string | null>(null);

  const approve = useCallback(async () => {
    setBusy('approve');
    setErr(null);
    try {
      await tenantApi.reviews.approve({
        review_id: review.id,
        comment: comment.trim() ? comment.trim() : undefined,
      });
      setComment('');
      await onChanged();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  }, [token, review.id, comment, onChanged]);

  const reject = useCallback(async () => {
    if (!comment.trim()) {
      setErr('Ange en kommentar när du begär ändringar.');
      return;
    }
    setBusy('reject');
    setErr(null);
    try {
      await tenantApi.reviews.reject({
        review_id: review.id,
        comment: comment.trim(),
      });
      setComment('');
      await onChanged();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  }, [token, review.id, comment, onChanged]);

  const cancel = useCallback(async () => {
    setBusy('cancel');
    setErr(null);
    try {
      await tenantApi.reviews.cancel({ review_id: review.id });
      await onChanged();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  }, [token, review.id, onChanged]);

  return (
    <div className="flex flex-col gap-2 rounded border border-purple-300 bg-purple-50 p-3">
      <div className="text-xs">
        <span className="font-medium text-purple-900">
          {isReviewer
            ? 'Du har bett att granska detta avtal.'
            : `Skickat till ${review.reviewer.name ?? review.reviewer.email} för granskning.`}
        </span>
        <div className="text-[11px] text-purple-800/80">
          Begärt av {review.requester.name ?? review.requester.email} ·{' '}
          {new Date(review.requestedAt).toLocaleString('sv-SE')}
        </div>
      </div>
      {review.requestComment && (
        <div className="rounded border border-purple-200 bg-white p-2 text-xs text-stone-700">
          <div className="mb-1 text-[10px] font-medium uppercase tracking-wider text-stone-500">
            Kommentar från avsändaren
          </div>
          {review.requestComment}
        </div>
      )}

      {isReviewer && (
        <>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Kommentar (obligatorisk vid begäran om ändring)"
            className="min-h-[64px] rounded border border-stone-300 bg-white px-2 py-1 text-sm"
            disabled={busy !== null}
          />
          {err && <div className="text-xs text-red-700">{err}</div>}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={approve}
              disabled={busy !== null}
              className="rounded bg-green-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-800 disabled:opacity-50"
            >
              {busy === 'approve' ? 'Godkänner…' : 'Godkänn'}
            </button>
            <button
              type="button"
              onClick={reject}
              disabled={busy !== null}
              className="rounded bg-red-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-800 disabled:opacity-50"
            >
              {busy === 'reject' ? 'Skickar…' : 'Begär ändring'}
            </button>
          </div>
        </>
      )}

      {!isReviewer && isRequester && (
        <>
          {err && <div className="text-xs text-red-700">{err}</div>}
          <button
            type="button"
            onClick={cancel}
            disabled={busy !== null}
            className="self-start rounded border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-stone-800 hover:bg-stone-50 disabled:opacity-50"
          >
            {busy === 'cancel' ? 'Avbryter…' : 'Avbryt granskning'}
          </button>
        </>
      )}
    </div>
  );
}

export function ClosedReviewBanner({
  review,
  variant,
}: {
  review: ContractReview;
  variant: 'approved' | 'rejected';
}) {
  const isApproved = variant === 'approved';
  const cls = isApproved
    ? 'border-green-300 bg-green-50 text-green-900'
    : 'border-amber-300 bg-amber-50 text-amber-900';
  const label = isApproved ? 'Avtalet godkändes' : 'Granskaren begärde ändringar';
  return (
    <div className={`rounded border p-2 text-xs ${cls}`}>
      <div className="font-medium">{label}</div>
      <div className="text-[11px] opacity-80">
        {review.reviewer.name ?? review.reviewer.email} ·{' '}
        {review.respondedAt
          ? new Date(review.respondedAt).toLocaleString('sv-SE')
          : ''}
      </div>
      {review.responseComment && (
        <div className="mt-1 whitespace-pre-wrap rounded bg-white/60 p-2 text-stone-800">
          {review.responseComment}
        </div>
      )}
    </div>
  );
}

function ReviewHistory({
  reviews,
  activeReviewId,
  open = false,
}: {
  reviews: ContractReview[];
  activeReviewId: string | null;
  open?: boolean;
}) {
  const closed = reviews.filter((r) => r.id !== activeReviewId);
  if (closed.length === 0) return null;
  return (
    <details className="text-xs" open={open}>
      <summary className="cursor-pointer text-stone-500 hover:text-stone-700">
        Granskningshistorik ({closed.length})
      </summary>
      <ul className="mt-2 space-y-2">
        {closed.map((r) => (
          <li key={r.id} className="rounded border border-stone-200 bg-white p-2">
            <div className="flex items-baseline justify-between">
              <span className="font-medium text-stone-700">
                {STATUS_LABEL[r.status]}
              </span>
              <span className="text-[10px] text-stone-500">
                {r.respondedAt
                  ? new Date(r.respondedAt).toLocaleString('sv-SE')
                  : new Date(r.requestedAt).toLocaleString('sv-SE')}
              </span>
            </div>
            <div className="text-[11px] text-stone-500">
              {r.reviewer.name ?? r.reviewer.email}
              {' ← '}
              {r.requester.name ?? r.requester.email}
            </div>
            {r.responseComment && (
              <div className="mt-1 whitespace-pre-wrap text-stone-700">
                {r.responseComment}
              </div>
            )}
          </li>
        ))}
      </ul>
    </details>
  );
}

const STATUS_LABEL: Record<ContractReview['status'], string> = {
  pending: 'Väntande',
  approved: 'Godkänd',
  rejected: 'Ändringar begärda',
  cancelled: 'Avbruten',
  superseded: 'Godkännande återställt',
};
